import {
  COMMAND_VOCABULARY,
  type CommandVocabularyEntry,
  type CommandVocabularyArticulation,
  type CommandVocabularyHandle,
  type CommandVocabularyRegister,
} from "../content/command-vocabulary";

export type CommandVocabularyAccess = "free" | "plus";
export type CommandVocabularyDifficulty =
  | "foundation"
  | "bright-frontier"
  | "mid-frontier"
  | "deep-frontier";

export type CommandVocabularyCatalogEntry = {
  id: string;
  word: string;
  sourceSenseId: string;
  partOfSpeech: string;
  difficulty: CommandVocabularyDifficulty;
  handles: CommandVocabularyHandle[];
  registers: CommandVocabularyRegister[];
  articulations: CommandVocabularyArticulation[];
  relatedWords: string[];
  packId: string;
  access: CommandVocabularyAccess;
  manifestSha256: string | null;
  packContentSha256: string | null;
};

type PublicPackEvidence = {
  packId: string;
  countedFamilyCount: 50;
  contentSha256: string;
};

type PublicIndex = {
  schemaVersion: "command-vocabulary-index-v2";
  permanentFreeCount: 50;
  plusCount: 2_000;
  totalCount: 2_050;
  searchableRelatedFormCount: number;
  manifestSha256: string;
  packs: PublicPackEvidence[];
  contentSha256: string;
  entries: Array<Omit<
    CommandVocabularyCatalogEntry,
    "id" | "manifestSha256" | "packContentSha256"
  >>;
};

type AuthoredEntry = {
  dictionaryIdentity: { word: string; lemma: string };
  readablePronunciation: string;
  pronunciationProvenance: {
    source: string;
    values: Array<{ value: string; variety?: string }>;
  };
  centralMeanings: Array<{
    definition: string;
    partOfSpeech: string;
    sourceSenseIds: string[];
  }>;
  relatedFamilyMembers: Array<{
    word: string;
    partOfSpeech: string;
    sourceSenseIds: string[];
    relationship: string;
    meaning: string;
    example: string;
  }>;
  handles: CommandVocabularyHandle[];
  registers: CommandVocabularyRegister[];
  whyWorthHaving: string;
  distinction: { nearbyWord: string; explanation: string };
  combinations: string[];
  examples: Array<{ context: string; sentence: string }>;
  landingRange: CommandVocabularyEntry["landingRange"];
  commonMisuse: string | null;
};

type StaticPack = {
  schemaVersion: "command-vocabulary-static-pack-v2";
  packId: string;
  access: "plus";
  countedFamilyCount: 50;
  manifestSha256: string;
  contentSha256: string;
  entries: Record<string, AuthoredEntry>;
};

export const FREE_COMMAND_VOCABULARY = COMMAND_VOCABULARY.filter((entry) => entry.isFree);

export const INITIAL_COMMAND_VOCABULARY_INDEX: CommandVocabularyCatalogEntry[] =
  FREE_COMMAND_VOCABULARY.map((entry) => ({
    id: entry.id,
    word: entry.word,
    sourceSenseId: entry.dictionaryIdentity.sourceSenseId,
    partOfSpeech: entry.partOfSpeech,
    difficulty: "foundation",
    handles: entry.handles,
    registers: entry.registers,
    articulations: entry.articulations,
    relatedWords: [],
    packId: "free-foundation",
    access: "free",
    manifestSha256: null,
    packContentSha256: null,
  }));

const packRequests = new Map<string, Promise<StaticPack>>();
const SHA256_PATTERN = /^[a-f0-9]{64}$/;

export function orderCommandVocabularyForLearning(
  entries: CommandVocabularyCatalogEntry[],
): CommandVocabularyCatalogEntry[] {
  return [
    ...entries.filter((entry) => entry.access === "free"),
    ...entries.filter((entry) => entry.access === "plus"),
  ];
}

export async function loadCommandVocabularyIndex(): Promise<CommandVocabularyCatalogEntry[]> {
  const response = await fetch("/vocabulary/index.json", { cache: "no-cache" });
  if (!response.ok) throw new Error("The command-vocabulary index is unavailable.");
  const index = await response.json() as PublicIndex;
  if (!index
    || index.schemaVersion !== "command-vocabulary-index-v2"
    || index.permanentFreeCount !== 50
    || index.plusCount !== 2_000
    || index.totalCount !== 2_050
    || !Number.isInteger(index.searchableRelatedFormCount)
    || index.searchableRelatedFormCount < 0
    || !isSha256(index.manifestSha256)
    || !isSha256(index.contentSha256)
    || !Array.isArray(index.packs)
    || index.packs.length !== 40
    || !Array.isArray(index.entries)
    || index.entries.length !== 2_050) {
    throw new Error("The command-vocabulary index failed its release contract.");
  }

  const { contentSha256, ...indexWithoutDigest } = index;
  if (await sha256Json(indexWithoutDigest) !== contentSha256) {
    throw new Error("The command-vocabulary index failed its integrity check.");
  }

  const packDigests = new Map<string, string>();
  for (const evidence of index.packs) {
    if (!evidence
      || typeof evidence.packId !== "string"
      || !evidence.packId
      || evidence.packId === "free-foundation"
      || evidence.countedFamilyCount !== 50
      || !isSha256(evidence.contentSha256)
      || packDigests.has(evidence.packId)) {
      throw new Error("The command-vocabulary index failed its pack-evidence contract.");
    }
    packDigests.set(evidence.packId, evidence.contentSha256);
  }

  const packEntryCounts = new Map<string, number>();
  let freeCount = 0;
  let plusCount = 0;
  for (const entry of index.entries) {
    if (!entry || typeof entry.word !== "string" || !entry.word) {
      throw new Error("The command-vocabulary index failed its release contract.");
    }
    if (entry.access === "free" && entry.packId === "free-foundation") {
      freeCount += 1;
    } else if (entry.access === "plus" && packDigests.has(entry.packId)) {
      plusCount += 1;
      packEntryCounts.set(entry.packId, (packEntryCounts.get(entry.packId) || 0) + 1);
    } else {
      throw new Error("The command-vocabulary index failed its pack-evidence contract.");
    }
  }
  if (freeCount !== index.permanentFreeCount
    || plusCount !== index.plusCount
    || [...packDigests.keys()].some((packId) => packEntryCounts.get(packId) !== 50)) {
    throw new Error("The command-vocabulary index failed its pack-evidence contract.");
  }

  const entries = index.entries.map((entry) => ({
    ...entry,
    id: entry.word,
    manifestSha256: index.manifestSha256,
    packContentSha256: entry.access === "plus" ? packDigests.get(entry.packId)! : null,
  }));
  if (new Set(entries.map(({ id }) => id)).size !== 2_050) {
    throw new Error("The command-vocabulary index contains duplicate words.");
  }
  return orderCommandVocabularyForLearning(entries);
}

export async function loadCommandVocabularyEntry(meta: CommandVocabularyCatalogEntry) {
  if (meta.access === "free") {
    return FREE_COMMAND_VOCABULARY.find((entry) => entry.id === meta.id) || null;
  }
  const requestKey = `${meta.packId}:${meta.manifestSha256}:${meta.packContentSha256}`;
  let request = packRequests.get(requestKey);
  if (!request) {
    request = loadPack(meta);
    packRequests.set(requestKey, request);
  }
  try {
    const pack = await request;
    const authored = pack.entries[meta.word];
    if (!authored) throw new Error("That lesson is absent from its vocabulary pack.");
    return normalize(meta, authored);
  } catch (error) {
    if (packRequests.get(requestKey) === request) packRequests.delete(requestKey);
    throw error;
  }
}

async function loadPack(meta: CommandVocabularyCatalogEntry) {
  const { packId, manifestSha256, packContentSha256 } = meta;
  if (!isSha256(manifestSha256) || !isSha256(packContentSha256)) {
    throw new Error("The command-vocabulary index failed its pack-evidence contract.");
  }
  const accessResponse = await fetch(
    `/api/vocabulary/packs/${encodeURIComponent(packId)}?digest=${packContentSha256}`,
    {
      credentials: "same-origin",
      cache: "no-store",
    },
  );
  if (!accessResponse.ok) {
    throw new Error(accessResponse.status === 403
      ? "Verbalore Plus is required for this lesson."
      : "This vocabulary pack could not be opened yet.");
  }
  const access = await accessResponse.json() as {
    packId?: string;
    contentSha256?: string;
    url?: string;
  };
  if (access.packId !== packId
    || access.contentSha256 !== packContentSha256
    || !access.url) {
    throw new Error("The vocabulary access response is invalid.");
  }
  const packResponse = await fetch(access.url, { cache: "force-cache" });
  if (!packResponse.ok) throw new Error("This vocabulary pack could not be delivered.");
  const pack = await packResponse.json() as StaticPack;
  if (!pack
    || pack.schemaVersion !== "command-vocabulary-static-pack-v2"
    || pack.packId !== packId
    || pack.access !== "plus"
    || pack.countedFamilyCount !== 50
    || !isSha256(pack.manifestSha256)
    || !isSha256(pack.contentSha256)
    || !pack.entries
    || Array.isArray(pack.entries)
    || Object.keys(pack.entries).length !== 50) {
    throw new Error("The vocabulary pack failed its release contract.");
  }
  const { contentSha256, ...packWithoutDigest } = pack;
  if (pack.manifestSha256 !== manifestSha256
    || contentSha256 !== packContentSha256
    || await sha256Json(packWithoutDigest) !== contentSha256) {
    throw new Error("The vocabulary pack failed its integrity check.");
  }
  return pack;
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && SHA256_PATTERN.test(value);
}

async function sha256Json(value: object) {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Vocabulary integrity verification is unavailable in this browser.");
  }
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function normalize(
  meta: CommandVocabularyCatalogEntry,
  authored: AuthoredEntry,
): CommandVocabularyEntry {
  const primary = authored.centralMeanings[0];
  if (!primary
    || authored.dictionaryIdentity.word !== meta.word
    || primary.partOfSpeech !== meta.partOfSpeech
    || !primary.sourceSenseIds.includes(meta.sourceSenseId)) {
    throw new Error("The lesson does not match its central dictionary identity.");
  }
  const sourceSentence = authored.examples[0]?.sentence || "";
  return {
    id: meta.id,
    word: meta.word,
    dictionaryIdentity: {
      lemma: authored.dictionaryIdentity.lemma,
      sourceSenseId: meta.sourceSenseId,
    },
    phonetic: authored.readablePronunciation,
    pronunciationProvenance: authored.pronunciationProvenance,
    partOfSpeech: primary.partOfSpeech,
    meaning: primary.definition,
    alternateMeaning: authored.centralMeanings[1]?.definition,
    synonym: "",
    antonym: "",
    family: authored.relatedFamilyMembers.map(({ word }) => word).join(" · "),
    relatedFamilyMembers: authored.relatedFamilyMembers,
    whyWorthHaving: authored.whyWorthHaving,
    handles: authored.handles,
    registers: authored.registers,
    articulations: authored.landingRange.options.map(({ label }) =>
      label.toLocaleLowerCase().replaceAll(" ", "-") as CommandVocabularyArticulation
    ),
    distinction: authored.distinction,
    combinations: authored.combinations,
    examples: authored.examples,
    landingRange: authored.landingRange,
    commonMisuse: authored.commonMisuse,
    sourceReadingId: `command-vocabulary:${meta.sourceSenseId}`,
    sourceReadingTitle: "Words Worth Having",
    sourceParagraphId: "p1",
    sourceSentence,
    isFree: false,
  };
}
